import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Truck,
  Clock,
  CheckCircle,
  XCircle,
  Package,
  MapPin,
  Calendar,
  User,
  Check,
  X,
  AlertTriangle,
  Box,
  Edit3,
  Eye,
  RefreshCw,
} from "lucide-react";

export default function AdminRequisiciones() {
  const [requisiciones, setRequisiciones] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("pendiente");
  const [selectedReq, setSelectedReq] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("view"); // view, approve, reject
  const [notas, setNotas] = useState("");
  const [processing, setProcessing] = useState(false);
  const [editedItems, setEditedItems] = useState([]);

  useEffect(() => {
    fetchData();
  }, [filtro]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reqRes, prodRes] = await Promise.all([
        api.get("/requisiciones", { params: filtro !== "todas" ? { estado: filtro } : {} }),
        api.get("/productos"),
      ]);
      setRequisiciones(reqRes.data);
      setProductos(prodRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const getProductStock = (productoId) => {
    const prod = productos.find(p => p.id === productoId);
    return prod?.stock_central || 0;
  };

  const openDialog = (req, mode) => {
    setSelectedReq(req);
    setDialogMode(mode);
    setNotas("");
    if (mode === "approve") {
      setEditedItems(req.items.map(item => ({
        ...item,
        cantidad_aprobada: Math.min(item.cantidad, getProductStock(item.producto_id)),
        stock_disponible: getProductStock(item.producto_id)
      })));
    }
    setDialogOpen(true);
  };

  const handleUpdateItemQuantity = (index, newQuantity) => {
    const updated = [...editedItems];
    const maxQty = updated[index].stock_disponible;
    updated[index].cantidad_aprobada = Math.max(0, Math.min(parseInt(newQuantity) || 0, maxQty));
    setEditedItems(updated);
  };

  const handleAprobar = async () => {
    setProcessing(true);
    try {
      const itemsToApprove = editedItems
        .filter(item => item.cantidad_aprobada > 0)
        .map(item => ({
          producto_id: item.producto_id,
          producto_nombre: item.producto_nombre,
          cantidad: item.cantidad_aprobada
        }));

      if (itemsToApprove.length === 0) {
        toast.error("Debe aprobar al menos un producto con cantidad mayor a 0");
        setProcessing(false);
        return;
      }

      await api.put(`/requisiciones/${selectedReq.id}/aprobar-con-cantidades`, {
        items: itemsToApprove,
        notas: notas
      });
      
      toast.success("✅ Requisición aprobada e inventario asignado al vendedor");
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al aprobar la requisición";
      toast.error(`❌ ${errorMsg}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleRechazar = async () => {
    if (!notas.trim()) {
      toast.error("Por favor indique el motivo del rechazo");
      return;
    }
    
    setProcessing(true);
    try {
      await api.put(`/requisiciones/${selectedReq.id}/rechazar`, null, {
        params: { notas: notas }
      });
      toast.success("Requisición rechazada");
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Error al rechazar la requisición");
    } finally {
      setProcessing(false);
    }
  };

  const handleDespachar = async (id) => {
    setProcessing(true);
    try {
      await api.put(`/requisiciones/${id}/despachar`);
      toast.success("✅ Requisición marcada como despachada");
      fetchData();
    } catch (error) {
      toast.error("Error al despachar");
    } finally {
      setProcessing(false);
    }
  };

  const getEstadoBadge = (estado) => {
    const config = {
      pendiente: { color: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock, text: "Pendiente" },
      aprobada: { color: "bg-blue-100 text-blue-700 border-blue-200", icon: CheckCircle, text: "Aprobada" },
      despachada: { color: "bg-green-100 text-green-700 border-green-200", icon: Truck, text: "Despachada" },
      rechazada: { color: "bg-red-100 text-red-700 border-red-200", icon: XCircle, text: "Rechazada" },
    };
    const { color, icon: Icon, text } = config[estado] || config.pendiente;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${color}`}>
        <Icon className="w-4 h-4" />
        {text}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-CO", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const contarPorEstado = (estado) => {
    return requisiciones.filter(r => r.estado === estado).length;
  };

  const pendientes = requisiciones.filter(r => r.estado === "pendiente").length;

  return (
    <div className="space-y-6" data-testid="admin-requisiciones">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-display text-3xl text-torta-dark">Requisiciones</h1>
          <p className="text-torta-muted">Gestiona las solicitudes de inventario de tus vendedores</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div 
          onClick={() => setFiltro("pendiente")}
          className={`bg-white rounded-xl p-4 shadow-sm cursor-pointer transition-all hover:shadow-md ${filtro === "pendiente" ? "ring-2 ring-amber-400" : ""}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-torta-dark">{contarPorEstado("pendiente")}</p>
              <p className="text-xs text-torta-muted">Pendientes</p>
            </div>
          </div>
        </div>

        <div 
          onClick={() => setFiltro("aprobada")}
          className={`bg-white rounded-xl p-4 shadow-sm cursor-pointer transition-all hover:shadow-md ${filtro === "aprobada" ? "ring-2 ring-blue-400" : ""}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-torta-dark">{contarPorEstado("aprobada")}</p>
              <p className="text-xs text-torta-muted">Aprobadas</p>
            </div>
          </div>
        </div>

        <div 
          onClick={() => setFiltro("despachada")}
          className={`bg-white rounded-xl p-4 shadow-sm cursor-pointer transition-all hover:shadow-md ${filtro === "despachada" ? "ring-2 ring-green-400" : ""}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Truck className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-torta-dark">{contarPorEstado("despachada")}</p>
              <p className="text-xs text-torta-muted">Despachadas</p>
            </div>
          </div>
        </div>

        <div 
          onClick={() => setFiltro("todas")}
          className={`bg-white rounded-xl p-4 shadow-sm cursor-pointer transition-all hover:shadow-md ${filtro === "todas" ? "ring-2 ring-torta-brown" : ""}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-torta-cream rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-torta-brown" />
            </div>
            <div>
              <p className="text-2xl font-bold text-torta-dark">{requisiciones.length}</p>
              <p className="text-xs text-torta-muted">Todas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Requisiciones List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-torta-brown">Cargando requisiciones...</div>
        </div>
      ) : requisiciones.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
          <Package className="w-16 h-16 text-torta-muted mx-auto mb-4" />
          <p className="text-torta-dark font-medium text-lg">No hay requisiciones {filtro !== "todas" ? filtro + "s" : ""}</p>
          <p className="text-torta-muted text-sm mt-1">Las solicitudes de inventario aparecerán aquí</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requisiciones.map((req) => (
            <div
              key={req.id}
              className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`requisicion-${req.id}`}
            >
              {/* Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-torta-cream rounded-xl flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6 text-torta-brown" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-torta-dark">{req.vendedor_nombre}</h3>
                    <p className="text-sm text-torta-muted">
                      Solicitado: {formatDate(req.fecha_creacion)}
                    </p>
                  </div>
                </div>
                {getEstadoBadge(req.estado)}
              </div>

              {/* Items */}
              <div className="bg-torta-cream/30 rounded-xl p-4 mb-4">
                <p className="text-sm font-medium text-torta-dark mb-3 flex items-center gap-2">
                  <Box className="w-4 h-4" />
                  Productos solicitados:
                </p>
                <div className="grid gap-2">
                  {req.items.map((item, i) => {
                    const stock = getProductStock(item.producto_id);
                    const stockInsuficiente = stock < item.cantidad;
                    return (
                      <div
                        key={i}
                        className={`flex justify-between items-center p-3 rounded-lg ${
                          stockInsuficiente && req.estado === "pendiente" 
                            ? "bg-red-50 border border-red-200" 
                            : "bg-white"
                        }`}
                      >
                        <span className="font-medium text-torta-dark">{item.producto_nombre}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-torta-dark font-bold">{item.cantidad} uds</span>
                          {req.estado === "pendiente" && (
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              stockInsuficiente 
                                ? "bg-red-100 text-red-700" 
                                : "bg-green-100 text-green-700"
                            }`}>
                              Stock: {stock}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Delivery Info */}
              <div className="flex flex-wrap gap-4 mb-4 text-sm">
                <div className="flex items-center gap-2 text-torta-muted">
                  <MapPin className="w-4 h-4" />
                  <span>{req.direccion_entrega}</span>
                </div>
                <div className="flex items-center gap-2 text-torta-muted">
                  <Calendar className="w-4 h-4" />
                  <span>Entrega: {req.fecha_requerida}</span>
                </div>
              </div>

              {req.notas && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-amber-800">
                    <strong>Nota del vendedor:</strong> {req.notas}
                  </p>
                </div>
              )}

              {req.notas_admin && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Respuesta admin:</strong> {req.notas_admin}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-3 border-t border-torta-sand">
                {req.estado === "pendiente" && (
                  <>
                    <Button
                      onClick={() => openDialog(req, "approve")}
                      className="bg-green-600 hover:bg-green-700 gap-2"
                      data-testid={`aprobar-${req.id}`}
                    >
                      <Check className="w-4 h-4" />
                      Aprobar
                    </Button>
                    <Button
                      onClick={() => openDialog(req, "reject")}
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50 gap-2"
                      data-testid={`rechazar-${req.id}`}
                    >
                      <X className="w-4 h-4" />
                      Rechazar
                    </Button>
                  </>
                )}
                {req.estado === "aprobada" && (
                  <Button
                    onClick={() => handleDespachar(req.id)}
                    disabled={processing}
                    className="bg-blue-600 hover:bg-blue-700 gap-2"
                  >
                    <Truck className="w-4 h-4" />
                    Marcar como Despachada
                  </Button>
                )}
                <Button
                  onClick={() => openDialog(req, "view")}
                  variant="ghost"
                  className="gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Ver detalles
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              {dialogMode === "approve" && <CheckCircle className="w-6 h-6 text-green-600" />}
              {dialogMode === "reject" && <XCircle className="w-6 h-6 text-red-600" />}
              {dialogMode === "view" && <Eye className="w-6 h-6 text-torta-brown" />}
              {dialogMode === "approve" && "Aprobar Requisición"}
              {dialogMode === "reject" && "Rechazar Requisición"}
              {dialogMode === "view" && "Detalles de Requisición"}
            </DialogTitle>
            <DialogDescription>
              Requisición de <strong>{selectedReq?.vendedor_nombre}</strong>
            </DialogDescription>
          </DialogHeader>

          {/* View Mode */}
          {dialogMode === "view" && selectedReq && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-torta-muted">Estado:</span>
                {getEstadoBadge(selectedReq.estado)}
              </div>
              
              <div>
                <p className="text-sm font-medium text-torta-dark mb-2">Productos:</p>
                {selectedReq.items.map((item, i) => (
                  <div key={i} className="flex justify-between py-2 border-b border-torta-sand last:border-0">
                    <span>{item.producto_nombre}</span>
                    <span className="font-medium">{item.cantidad} uds</span>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-torta-muted">Dirección:</p>
                  <p className="font-medium">{selectedReq.direccion_entrega}</p>
                </div>
                <div>
                  <p className="text-torta-muted">Fecha requerida:</p>
                  <p className="font-medium">{selectedReq.fecha_requerida}</p>
                </div>
              </div>

              {selectedReq.notas && (
                <div className="bg-torta-cream/50 rounded-lg p-3">
                  <p className="text-sm"><strong>Notas:</strong> {selectedReq.notas}</p>
                </div>
              )}
            </div>
          )}

          {/* Approve Mode */}
          {dialogMode === "approve" && selectedReq && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Ajusta las cantidades según el stock disponible
                </p>
              </div>

              <div className="space-y-3">
                {editedItems.map((item, i) => {
                  const stockInsuficiente = item.stock_disponible < item.cantidad;
                  return (
                    <div
                      key={i}
                      className={`p-4 rounded-xl border-2 ${
                        stockInsuficiente ? "border-red-200 bg-red-50" : "border-torta-sand bg-white"
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-medium text-torta-dark">{item.producto_nombre}</p>
                          <p className="text-sm text-torta-muted">
                            Solicitado: {item.cantidad} uds
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          item.stock_disponible > 0 
                            ? "bg-green-100 text-green-700" 
                            : "bg-red-100 text-red-700"
                        }`}>
                          Stock: {item.stock_disponible}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <label className="text-sm text-torta-muted whitespace-nowrap">
                          Aprobar:
                        </label>
                        <Input
                          type="number"
                          min="0"
                          max={item.stock_disponible}
                          value={item.cantidad_aprobada}
                          onChange={(e) => handleUpdateItemQuantity(i, e.target.value)}
                          className="w-24"
                        />
                        <span className="text-sm text-torta-muted">
                          de {item.stock_disponible} disponibles
                        </span>
                      </div>
                      
                      {stockInsuficiente && (
                        <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Stock insuficiente para la cantidad solicitada
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              <div>
                <label className="text-sm font-medium text-torta-dark mb-2 block">
                  Notas para el vendedor (opcional):
                </label>
                <Textarea
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  placeholder="Ej: Se aprobaron menos unidades por stock limitado..."
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Reject Mode */}
          {dialogMode === "reject" && selectedReq && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Esta acción notificará al vendedor que su solicitud fue rechazada
                </p>
              </div>

              <div className="bg-torta-cream/50 rounded-lg p-4">
                <p className="text-sm font-medium mb-2">Productos solicitados:</p>
                {selectedReq.items.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm py-1">
                    <span>{item.producto_nombre}</span>
                    <span>{item.cantidad} uds</span>
                  </div>
                ))}
              </div>

              <div>
                <label className="text-sm font-medium text-torta-dark mb-2 block">
                  Motivo del rechazo <span className="text-red-500">*</span>:
                </label>
                <Textarea
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  placeholder="Ej: No hay stock disponible, fuera de zona de cobertura..."
                  rows={3}
                  required
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            {dialogMode === "approve" && (
              <Button
                onClick={handleAprobar}
                disabled={processing || editedItems.every(i => i.cantidad_aprobada === 0)}
                className="bg-green-600 hover:bg-green-700"
              >
                {processing ? "Procesando..." : "Confirmar Aprobación"}
              </Button>
            )}
            {dialogMode === "reject" && (
              <Button
                onClick={handleRechazar}
                disabled={processing || !notas.trim()}
                className="bg-red-600 hover:bg-red-700"
              >
                {processing ? "Procesando..." : "Confirmar Rechazo"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
